import React, { forwardRef } from "react";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const InvoicePaper = forwardRef(function InvoicePaper(
  { data, subtotal, gst, grandTotal, formatINR },
  ref
) {
  const accentColor = (data.brandColorEnabled && data.brandColor) ? data.brandColor : "#002FA7";

  const styles = {
    paper: {
      width: "210mm",
      minHeight: "297mm",
      boxSizing: "border-box",
      backgroundColor: "#ffffff",
      padding: "14mm 16mm 12mm",
      fontFamily: "'Inter', sans-serif",
      color: "#111827",
      fontSize: "12.5px",
      lineHeight: 1.5,
      boxShadow: "0 20px 60px -15px rgba(0,0,0,0.12)",
      display: "flex",
      flexDirection: "column",
    },
    accentBar: {
      height: "5px",
      backgroundColor: accentColor,
      marginBottom: "10mm",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "9mm",
    },
    businessName: {
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 700,
      fontSize: "17px",
      color: "#111827",
      marginTop: "4px",
    },
    senderSub: {
      fontSize: "11.5px",
      color: "#6b7280",
      marginTop: "2px",
      whiteSpace: "pre-wrap",
    },
    invoiceTitle: {
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 800,
      fontSize: "30px",
      letterSpacing: "-0.02em",
      color: "#111827",
      lineHeight: 1,
      textAlign: "right",
    },
    invoiceNum: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "11.5px",
      color: "#6b7280",
      marginTop: "5px",
      textAlign: "right",
    },
    divider: {
      height: "1px",
      backgroundColor: "#e5e7eb",
      marginBottom: "8mm",
    },
    metaRow: {
      display: "grid",
      gridTemplateColumns: "1.4fr 1fr 1fr",
      gap: "20px",
      marginBottom: "9mm",
    },
    metaLabel: {
      fontSize: "9px",
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: "#9ca3af",
      marginBottom: "5px",
    },
    metaValue: {
      fontWeight: 600,
      fontSize: "12.5px",
      color: "#111827",
    },
    metaSubValue: {
      fontSize: "11.5px",
      color: "#6b7280",
      marginTop: "2px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: "8mm",
    },
    thCell: {
      padding: "7px 8px",
      fontSize: "9px",
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: "#9ca3af",
      borderBottom: "2px solid #111827",
      backgroundColor: "transparent",
    },
    tdCell: {
      padding: "9px 8px",
      fontSize: "12.5px",
      color: "#111827",
      borderBottom: "1px solid #f3f4f6",
      verticalAlign: "top",
    },
    monoCell: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "12px",
      color: "#374151",
    },
    totalsWrapper: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: "9mm",
    },
    totalsBox: {
      minWidth: "230px",
    },
    totalsRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "5px 0",
      borderBottom: "1px solid #f3f4f6",
    },
    totalLabel: {
      fontSize: "12px",
      color: "#6b7280",
    },
    totalValue: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "12px",
      color: "#374151",
    },
    grandTotalRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 12px",
      backgroundColor: "#111827",
      marginTop: "5px",
    },
    grandTotalLabel: {
      fontSize: "11.5px",
      fontWeight: 700,
      color: "#ffffff",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    grandTotalValue: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#ffffff",
    },
    sectionLabel: {
      fontSize: "9px",
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: "#9ca3af",
      marginBottom: "6px",
    },
    bodyText: {
      fontSize: "12px",
      color: "#374151",
      lineHeight: 1.65,
      whiteSpace: "pre-wrap",
    },
    paymentInfoWrapper: {
      marginBottom: "7mm",
      padding: "12px 16px",
      backgroundColor: "#f9fafb",
      borderRadius: "6px",
      border: "1px solid #e5e7eb",
    },
    bankDetailsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "8px 16px",
    },
    bankField: {
      display: "flex",
      flexDirection: "column",
    },
    bankFieldLabel: {
      fontSize: "8.5px",
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: "#9ca3af",
      marginBottom: "2px",
    },
    bankFieldValue: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "11.5px",
      color: "#111827",
      fontWeight: 500,
    },
    upiSection: {
      marginTop: "auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      paddingTop: "12mm",
      borderTop: "1px solid #e5e7eb",
      width: "100%",
    },
    upiQRImg: {
      width: "240px",
      height: "240px",
      objectFit: "contain",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      backgroundColor: "#ffffff",
      padding: "12px",
      marginTop: "12px",
      marginBottom: "12px",
    },
    upiLabel: {
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: "#9ca3af",
      textAlign: "center",
    },
    thankYouText: {
      fontSize: "12px",
      color: "#6b7280",
      fontStyle: "italic",
      lineHeight: 1.65,
      textAlign: "center",
      marginTop: "6mm",
    },
  };

  return (
    <div ref={ref} style={styles.paper} data-testid="invoice-paper">
      <div style={styles.accentBar} />

      <div style={styles.headerRow}>
        <div>
          {data.logo && (
            <img
              src={data.logo}
              alt="Logo"
              style={{
                height: "46px",
                width: "auto",
                objectFit: "contain",
                marginBottom: "8px",
              }}
              data-testid="invoice-logo"
            />
          )}
          <div style={styles.businessName} data-testid="invoice-business-name">
            {data.businessName || "Your Business Name"}
          </div>
          {data.senderName && (
            <div style={styles.senderSub} data-testid="invoice-sender-name">
              {data.senderName}
            </div>
          )}
        </div>
        <div>
          <div style={styles.invoiceTitle}>INVOICE</div>
          <div style={styles.invoiceNum} data-testid="invoice-number-display">
            {data.invoiceNumber}
          </div>
        </div>
      </div>

      <div style={styles.divider} />

      <div style={styles.metaRow}>
        <div>
          <div style={styles.metaLabel}>Bill To</div>
          <div style={{ ...styles.metaValue, whiteSpace: "pre-wrap" }} data-testid="invoice-client-name">
            {data.clientName || "—"}
          </div>
          {data.clientEmail && (
            <div style={styles.metaSubValue} data-testid="invoice-client-email">
              {data.clientEmail}
            </div>
          )}
        </div>
        <div>
          <div style={styles.metaLabel}>Invoice Date</div>
          <div style={styles.metaValue} data-testid="invoice-date-display">
            {formatDate(data.invoiceDate)}
          </div>
        </div>
        <div>
          <div style={styles.metaLabel}>Due Date</div>
          <div style={styles.metaValue} data-testid="invoice-due-date-display">
            {formatDate(data.dueDate)}
          </div>
        </div>
      </div>

      <table style={styles.table} data-testid="invoice-items-table">
        <thead>
          <tr>
            <th style={{ ...styles.thCell, textAlign: "left" }}>Description</th>
            <th style={{ ...styles.thCell, textAlign: "center", width: "56px" }}>Qty</th>
            <th style={{ ...styles.thCell, textAlign: "right", width: "110px" }}>Rate</th>
            <th style={{ ...styles.thCell, textAlign: "right", width: "110px" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item, idx) => (
            <tr key={item.id}>
              <td style={styles.tdCell}>{item.description || <span style={{ color: "#d1d5db" }}>—</span>}</td>
              <td style={{ ...styles.tdCell, ...styles.monoCell, textAlign: "center" }}>{item.qty}</td>
              <td style={{ ...styles.tdCell, ...styles.monoCell, textAlign: "right" }}>{formatINR(parseFloat(item.rate) || 0)}</td>
              <td style={{ ...styles.tdCell, ...styles.monoCell, textAlign: "right", fontWeight: 500, color: "#111827" }}>
                {formatINR((parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={styles.totalsWrapper}>
        <div style={styles.totalsBox}>
          <div style={styles.totalsRow}>
            <span style={styles.totalLabel}>Subtotal</span>
            <span style={styles.totalValue}>{formatINR(subtotal)}</span>
          </div>
          {data.gstEnabled && (
            <div style={styles.totalsRow}>
              <span style={styles.totalLabel}>GST ({parseFloat(data.gstRate) || 0}%)</span>
              <span style={styles.totalValue}>{formatINR(gst)}</span>
            </div>
          )}
          <div style={styles.grandTotalRow}>
            <span style={styles.grandTotalLabel}>Grand Total</span>
            <span style={styles.grandTotalValue}>{formatINR(grandTotal)}</span>
          </div>
        </div>
      </div>

      <div style={styles.divider} />

      {data.paymentTerms && (
        <div style={{ marginBottom: "7mm" }}>
          <div style={styles.sectionLabel}>Payment Terms</div>
          <div style={styles.bodyText}>{data.paymentTerms}</div>
        </div>
      )}

      {data.bankEnabled && (
        <div style={styles.paymentInfoWrapper}>
          <div style={{ ...styles.sectionLabel, marginBottom: "8px" }}>Bank Details</div>
          <div style={styles.bankDetailsGrid}>
            {data.bankHolderName && (
              <div style={styles.bankField}>
                <span style={styles.bankFieldLabel}>Account Holder</span>
                <span style={styles.bankFieldValue}>{data.bankHolderName}</span>
              </div>
            )}
            {data.bankAccountNumber && (
              <div style={styles.bankField}>
                <span style={styles.bankFieldLabel}>Account Number</span>
                <span style={styles.bankFieldValue}>{data.bankAccountNumber}</span>
              </div>
            )}
            {data.bankName && (
              <div style={styles.bankField}>
                <span style={styles.bankFieldLabel}>Bank & Branch</span>
                <span style={styles.bankFieldValue}>{data.bankName}</span>
              </div>
            )}
            {data.bankIFSC && (
              <div style={styles.bankField}>
                <span style={styles.bankFieldLabel}>IFSC Code</span>
                <span style={styles.bankFieldValue}>{data.bankIFSC}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {data.upiEnabled && data.upiQR && (
        <div style={styles.upiSection}>
          <div style={styles.upiLabel}>Scan to Pay</div>
          <img src={data.upiQR} alt="UPI QR Code" style={styles.upiQRImg} />
          <div style={styles.upiLabel}>UPI Payment</div>
        </div>
      )}

      {data.thankYouNote && (
        <div style={styles.thankYouText}>{data.thankYouNote}</div>
      )}
    </div>
  );
});

export default InvoicePaper;